import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { referralsAPI, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import {
  Copy, EnvelopeSimple, PaperPlaneTilt, WhatsappLogo,
  TwitterLogo, FacebookLogo, ChatCircle, QrCode, Check,
  UserPlus, Users, ArrowLeft, Share, Plus, X, LinkSimple
} from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";

export default function InviteFriendsPage() {
  const { user } = useAuth();
  const [referralCode, setReferralCode] = useState('');
  const [referralLink, setReferralLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [emailInputs, setEmailInputs] = useState(['']);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [tracking, setTracking] = useState({ invites: [], total_invited: 0, total_signups: 0 });
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [codeRes, trackingRes] = await Promise.all([
        referralsAPI.getMyCode(),
        referralsAPI.getTracking()
      ]);
      const code = codeRes.data.referral_code;
      setReferralCode(code);
      setReferralLink(`${window.location.origin}/register?ref=${code}`);
      setTracking(trackingRes.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Clipboard copy failed:', err);
      toast.error('Failed to copy');
    }
  };

  const handleAddEmail = () => {
    if (emailInputs.length < 10) {
      setEmailInputs([...emailInputs, '']);
    }
  };

  const handleRemoveEmail = (index) => {
    setEmailInputs(emailInputs.filter((_, i) => i !== index));
  };

  const handleEmailChange = (index, value) => {
    const updated = [...emailInputs];
    updated[index] = value;
    setEmailInputs(updated);
  };

  const handleSendEmails = async () => {
    const validEmails = emailInputs.filter(e => e.trim() && e.includes('@'));
    if (validEmails.length === 0) {
      toast.error('Enter at least one valid email');
      return;
    }
    setSendingEmails(true);
    try {
      const res = await referralsAPI.sendInviteEmail(validEmails);
      const { sent_count, results } = res.data;
      const alreadyRegistered = results.filter(r => r.status === 'already_registered');
      if (alreadyRegistered.length > 0) {
        toast.info(`${alreadyRegistered.length} already on Divispend`);
      }
      if (sent_count > 0) {
        toast.success(`${sent_count} invite${sent_count > 1 ? 's' : ''} sent!`);
      }
      setEmailInputs(['']);
      loadData();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendingEmails(false);
    }
  };

  const shareText = `Join me on Divispend — the easiest way to send money and sell your products! Sign up here: ${referralLink}`;

  const shareLinks = [
    {
      name: 'WhatsApp',
      icon: WhatsappLogo,
      color: 'bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366]/20',
      url: `https://wa.me/?text=${encodeURIComponent(shareText)}`
    },
    {
      name: 'Twitter / X',
      icon: TwitterLogo,
      color: 'bg-[#1DA1F2]/10 text-[#1DA1F2] hover:bg-[#1DA1F2]/20',
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`
    },
    {
      name: 'Facebook',
      icon: FacebookLogo,
      color: 'bg-[#1877F2]/10 text-[#1877F2] hover:bg-[#1877F2]/20',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`
    },
    {
      name: 'SMS',
      icon: ChatCircle,
      color: 'bg-[#FF9500]/10 text-[#FF9500] hover:bg-[#FF9500]/20',
      url: `sms:?body=${encodeURIComponent(shareText)}`
    }
  ];

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <div className="animate-pulse text-[#999999]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111111]">
      {/* Header */}
      <header className="bg-[#111111]/70 backdrop-blur-xl border-b border-[#333333] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Divispend" className="h-8 sm:h-10 w-auto" />
          </Link>
          <Link to="/dashboard">
            <Button variant="ghost" className="text-[#999999] hover:text-white hover:bg-[#333333] rounded-sm" data-testid="back-to-dashboard">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white flex items-center gap-3" style={{ fontFamily: 'Cabinet Grotesk' }}>
            <UserPlus className="w-7 h-7 text-[#22C55E]" />
            Invite Friends
          </h1>
          <p className="text-[#999999] mt-1">Share Divispend with your friends and grow your network</p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none" data-testid="stat-invites-sent">
            <CardContent className="p-5 text-center">
              <EnvelopeSimple className="w-6 h-6 text-[#22C55E] mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{tracking.total_invited}</p>
              <p className="text-xs text-[#999999] mt-1">Invites Sent</p>
            </CardContent>
          </Card>
          <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none" data-testid="stat-friends-joined">
            <CardContent className="p-5 text-center">
              <Users className="w-6 h-6 text-[#22C55E] mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{tracking.total_signups}</p>
              <p className="text-xs text-[#999999] mt-1">Friends Joined</p>
            </CardContent>
          </Card>
        </div>

        {/* Referral Link Card */}
        <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none mb-6" data-testid="referral-link-card">
          <CardHeader className="p-6 pb-3">
            <CardTitle className="text-lg font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
              <LinkSimple className="w-5 h-5 text-[#22C55E]" />
              Your Referral Link
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            <div className="flex gap-2">
              <Input
                value={referralLink}
                readOnly
                className="bg-[#111111] border-[#333333] text-white text-sm rounded-sm font-mono"
                data-testid="referral-link-input"
              />
              <Button
                onClick={handleCopy}
                className={`rounded-sm shrink-0 transition-all ${copied ? 'bg-[#22C55E] text-black' : 'bg-[#333333] hover:bg-[#444444] text-white'}`}
                data-testid="copy-referral-link"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-xs text-[#666666] mt-2">
              Your code: <span className="text-[#22C55E] font-mono font-bold">{referralCode}</span>
            </p>
          </CardContent>
        </Card>

        {/* Social Share */}
        <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none mb-6" data-testid="social-share-card">
          <CardHeader className="p-6 pb-3">
            <CardTitle className="text-lg font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
              <Share className="w-5 h-5 text-[#22C55E]" />
              Share via Social
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {shareLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex flex-col items-center gap-2 p-4 rounded-sm transition-colors ${link.color}`}
                  data-testid={`share-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <link.icon className="w-6 h-6" weight="fill" />
                  <span className="text-xs font-medium">{link.name}</span>
                </a>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Email Invite */}
        <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none mb-6" data-testid="email-invite-card">
          <CardHeader className="p-6 pb-3">
            <CardTitle className="text-lg font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
              <EnvelopeSimple className="w-5 h-5 text-[#22C55E]" />
              Invite by Email
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            <div className="space-y-3">
              {emailInputs.map((email, index) => (
                <div key={`email-input-${index}`} className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="friend@example.com"
                    value={email}
                    onChange={(e) => handleEmailChange(index, e.target.value)}
                    className="bg-[#111111] border-[#333333] text-white placeholder:text-[#666666] rounded-sm"
                    data-testid={`invite-email-input-${index}`}
                  />
                  {emailInputs.length > 1 && (
                    <Button
                      variant="ghost"
                      onClick={() => handleRemoveEmail(index)}
                      className="text-[#666666] hover:text-red-400 shrink-0 rounded-sm"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              {emailInputs.length < 10 && (
                <Button
                  variant="ghost"
                  onClick={handleAddEmail}
                  className="text-[#22C55E] hover:text-[#1EA34B] hover:bg-[#22C55E]/10 rounded-sm text-sm w-full"
                  data-testid="add-email-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add another email
                </Button>
              )}
            </div>
            <Button
              onClick={handleSendEmails}
              disabled={sendingEmails}
              className="w-full mt-4 bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm h-11 font-semibold"
              data-testid="send-invites-btn"
            >
              {sendingEmails ? (
                'Sending...'
              ) : (
                <>
                  <PaperPlaneTilt className="w-4 h-4 mr-2" />
                  Send Invites
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* QR Code */}
        <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none mb-6" data-testid="qr-code-card">
          <CardHeader className="p-6 pb-3">
            <CardTitle className="text-lg font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
              <QrCode className="w-5 h-5 text-[#22C55E]" />
              QR Code
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0 flex flex-col items-center">
            <div className="bg-white p-4 rounded-lg">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(referralLink)}&bgcolor=FFFFFF&color=111111`}
                alt="Referral QR Code"
                className="w-[200px] h-[200px]"
                data-testid="referral-qr-image"
              />
            </div>
            <p className="text-xs text-[#666666] mt-3">Scan to open your referral link</p>
          </CardContent>
        </Card>

        {/* Invite History */}
        {tracking.invites.length > 0 && (
          <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none" data-testid="invite-history-card">
            <CardHeader className="p-6 pb-3">
              <CardTitle className="text-lg font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk' }}>
                Invite History
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="space-y-3">
                {tracking.invites.map((invite, index) => (
                  <div
                    key={invite.invite_id || `invite-${index}`}
                    className="flex items-center justify-between py-3 border-b border-[#333333] last:border-0"
                    data-testid={`invite-history-item-${index}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                        invite.signed_up ? 'bg-[#22C55E]/10 text-[#22C55E]' : 'bg-[#333333] text-[#999999]'
                      }`}>
                        {invite.signed_up ? <Check className="w-4 h-4" /> : <EnvelopeSimple className="w-4 h-4" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-white truncate">{invite.email}</p>
                        <p className="text-xs text-[#666666]">{formatDate(invite.created_at)}</p>
                      </div>
                    </div>
                    <span className={`text-xs font-medium px-2 py-1 rounded-sm shrink-0 ${
                      invite.signed_up
                        ? 'bg-[#22C55E]/10 text-[#22C55E]'
                        : invite.status === 'sent'
                        ? 'bg-[#333333] text-[#999999]'
                        : 'bg-red-500/10 text-red-400'
                    }`}>
                      {invite.signed_up ? 'Joined' : invite.status === 'sent' ? 'Pending' : 'Failed'}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
